/* Domovoi · public holidays (pure; tested in tests/week.test.js).

   Canada by province or territory, the UK by nation, the US as one list.
   "Core" are the ones the law gives everyone there (the UK's bank holidays,
   the US's six most employers give); "others" are ones some people get,
   depending on where they work — they start unticked. Each person ticks
   their own (profiles.holidays_off, keys below); nothing here is a promise.

   A holiday that falls on a weekend moves: Canada and the UK to the next
   weekday that isn't already a holiday (Boxing Day on a Saturday → Monday 28),
   the US to the Friday before or the Monday after. */
import { iso, addDays, weekday } from './helpers.js';

const ymd = (y, m, d) => iso(new Date(y, m - 1, d));
/** The nth weekday (0 = Monday) of month m; n = -1 for the last. */
function nth(n, wd, m) {
  return (y) => {
    if (n > 0) {
      const first = ymd(y, m, 1);
      return addDays(first, ((wd - weekday(first) + 7) % 7) + (n - 1) * 7);
    }
    const last = addDays(ymd(m === 12 ? y + 1 : y, m === 12 ? 1 : m + 1, 1), -1);
    return addDays(last, -((weekday(last) - wd + 7) % 7));
  };
}
const fixed = (m, d) => (y) => ymd(y, m, d);
/** The Monday on or before m/d (Victoria Day: the Monday before 25 May). */
const mondayOnOrBefore = (m, d) => (y) => { const s = ymd(y, m, d); return addDays(s, -weekday(s)); };
/** Easter Sunday (Anonymous Gregorian algorithm). */
export function easter(y) {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
  return ymd(y, month, day);
}
const fromEaster = (n) => (y) => addDays(easter(y), n);

const RULES = {
  new_year: ['New Year’s Day', fixed(1, 1)],
  jan_2: ['2 January', fixed(1, 2)],
  family_day: ['Family Day', nth(3, 0, 2)],
  louis_riel: ['Louis Riel Day', nth(3, 0, 2)],
  islander_day: ['Islander Day', nth(3, 0, 2)],
  heritage_day_ns: ['Heritage Day', nth(3, 0, 2)],
  st_patrick: ['St Patrick’s Day', fixed(3, 17)],
  good_friday: ['Good Friday', fromEaster(-2)],
  easter_monday: ['Easter Monday', fromEaster(1)],
  victoria_day: ['Victoria Day', mondayOnOrBefore(5, 24)],
  patriots_day: ['National Patriots’ Day', mondayOnOrBefore(5, 24)],
  early_may: ['Early May bank holiday', nth(1, 0, 5)],
  spring_bank: ['Spring bank holiday', nth(-1, 0, 5)],
  indigenous_day: ['National Indigenous Peoples Day', fixed(6, 21)],
  st_jean: ['Fête nationale', fixed(6, 24)],
  canada_day: ['Canada Day', fixed(7, 1)],
  nunavut_day: ['Nunavut Day', fixed(7, 9)],
  boyne: ['Battle of the Boyne', fixed(7, 12)],
  civic_holiday: ['Civic Holiday', nth(1, 0, 8)],
  bc_day: ['BC Day', nth(1, 0, 8)],
  heritage_day_ab: ['Heritage Day', nth(1, 0, 8)],
  sask_day: ['Saskatchewan Day', nth(1, 0, 8)],
  terry_fox_day: ['Terry Fox Day', nth(1, 0, 8)],
  nb_day: ['New Brunswick Day', nth(1, 0, 8)],
  natal_day: ['Natal Day', nth(1, 0, 8)],
  summer_bank_scot: ['Summer bank holiday', nth(1, 0, 8)],
  summer_bank: ['Summer bank holiday', nth(-1, 0, 8)],
  discovery_day: ['Discovery Day', nth(3, 0, 8)],
  labour_day: ['Labour Day', nth(1, 0, 9)],
  truth_reconciliation: ['Truth and Reconciliation', fixed(9, 30)],
  thanksgiving_ca: ['Thanksgiving', nth(2, 0, 10)],
  remembrance_day: ['Remembrance Day', fixed(11, 11)],
  st_andrew: ['St Andrew’s Day', fixed(11, 30)],
  christmas_eve: ['Christmas Eve', fixed(12, 24)],
  christmas: ['Christmas Day', fixed(12, 25)],
  boxing_day: ['Boxing Day', fixed(12, 26)],
  // United States
  mlk_day: ['Martin Luther King Jr. Day', nth(3, 0, 1)],
  presidents_day: ['Presidents’ Day', nth(3, 0, 2)],
  memorial_day: ['Memorial Day', nth(-1, 0, 5)],
  juneteenth: ['Juneteenth', fixed(6, 19)],
  independence_day: ['Independence Day', fixed(7, 4)],
  labor_day: ['Labor Day', nth(1, 0, 9)],
  columbus_day: ['Columbus Day', nth(2, 0, 10)],
  veterans_day: ['Veterans Day', fixed(11, 11)],
  thanksgiving_us: ['Thanksgiving', nth(4, 3, 11)],
  day_after_thanksgiving: ['Day after Thanksgiving', (y) => addDays(nth(4, 3, 11)(y), 1)],
};
// Days that never move when they land on a weekend (they're the eve or the day after something).
const NO_SHIFT = new Set(['christmas_eve', 'day_after_thanksgiving']);

const FED = 'federally regulated jobs';
const CA_REGIONS = {
  'CA-ON': ['Ontario', ['new_year', 'family_day', 'good_friday', 'victoria_day', 'canada_day', 'labour_day', 'thanksgiving_ca', 'christmas', 'boxing_day'],
    ['remembrance_day', 'easter_monday', 'civic_holiday', 'truth_reconciliation'],
    { remembrance_day: FED, truth_reconciliation: FED, easter_monday: 'federal government', civic_holiday: 'most employers' }],
  'CA-QC': ['Quebec', ['new_year', 'good_friday', 'patriots_day', 'st_jean', 'canada_day', 'labour_day', 'thanksgiving_ca', 'christmas'],
    ['easter_monday', 'truth_reconciliation', 'remembrance_day', 'boxing_day'],
    { easter_monday: 'instead of Good Friday, at some jobs', truth_reconciliation: FED, remembrance_day: FED }],
  'CA-BC': ['British Columbia', ['new_year', 'family_day', 'good_friday', 'victoria_day', 'canada_day', 'bc_day', 'labour_day', 'truth_reconciliation', 'thanksgiving_ca', 'remembrance_day', 'christmas'],
    ['easter_monday', 'boxing_day'], {}],
  'CA-AB': ['Alberta', ['new_year', 'family_day', 'good_friday', 'victoria_day', 'canada_day', 'labour_day', 'thanksgiving_ca', 'remembrance_day', 'christmas'],
    ['easter_monday', 'heritage_day_ab', 'truth_reconciliation', 'boxing_day'], { truth_reconciliation: FED }],
  'CA-SK': ['Saskatchewan', ['new_year', 'family_day', 'good_friday', 'victoria_day', 'canada_day', 'sask_day', 'labour_day', 'thanksgiving_ca', 'remembrance_day', 'christmas'],
    ['easter_monday', 'truth_reconciliation', 'boxing_day'], { truth_reconciliation: FED }],
  'CA-MB': ['Manitoba', ['new_year', 'louis_riel', 'good_friday', 'victoria_day', 'canada_day', 'labour_day', 'truth_reconciliation', 'thanksgiving_ca', 'christmas'],
    ['easter_monday', 'terry_fox_day', 'remembrance_day', 'boxing_day'], { remembrance_day: FED }],
  'CA-NB': ['New Brunswick', ['new_year', 'family_day', 'good_friday', 'canada_day', 'nb_day', 'labour_day', 'remembrance_day', 'christmas'],
    ['easter_monday', 'victoria_day', 'truth_reconciliation', 'thanksgiving_ca', 'boxing_day'], { truth_reconciliation: FED }],
  'CA-NS': ['Nova Scotia', ['new_year', 'heritage_day_ns', 'good_friday', 'canada_day', 'labour_day', 'christmas'],
    ['easter_monday', 'victoria_day', 'natal_day', 'truth_reconciliation', 'thanksgiving_ca', 'remembrance_day', 'boxing_day'], { truth_reconciliation: FED, remembrance_day: FED }],
  'CA-PE': ['Prince Edward Island', ['new_year', 'islander_day', 'good_friday', 'canada_day', 'labour_day', 'truth_reconciliation', 'remembrance_day', 'christmas'],
    ['easter_monday', 'victoria_day', 'civic_holiday', 'thanksgiving_ca', 'boxing_day'], {}],
  'CA-NL': ['Newfoundland and Labrador', ['new_year', 'good_friday', 'canada_day', 'labour_day', 'remembrance_day', 'christmas'],
    ['victoria_day', 'truth_reconciliation', 'thanksgiving_ca', 'boxing_day'], { truth_reconciliation: FED }],
  'CA-YT': ['Yukon', ['new_year', 'good_friday', 'victoria_day', 'indigenous_day', 'canada_day', 'discovery_day', 'labour_day', 'truth_reconciliation', 'thanksgiving_ca', 'remembrance_day', 'christmas'],
    ['easter_monday', 'boxing_day'], {}],
  'CA-NT': ['Northwest Territories', ['new_year', 'good_friday', 'victoria_day', 'indigenous_day', 'canada_day', 'civic_holiday', 'labour_day', 'truth_reconciliation', 'thanksgiving_ca', 'remembrance_day', 'christmas', 'boxing_day'],
    ['easter_monday'], {}],
  'CA-NU': ['Nunavut', ['new_year', 'good_friday', 'victoria_day', 'canada_day', 'nunavut_day', 'civic_holiday', 'labour_day', 'thanksgiving_ca', 'remembrance_day', 'christmas', 'boxing_day'],
    ['easter_monday', 'truth_reconciliation'], { truth_reconciliation: FED }],
};
const UK_REGIONS = {
  'GB-ENG': ['England', ['new_year', 'good_friday', 'easter_monday', 'early_may', 'spring_bank', 'summer_bank', 'christmas', 'boxing_day'], [], {}],
  'GB-WLS': ['Wales', ['new_year', 'good_friday', 'easter_monday', 'early_may', 'spring_bank', 'summer_bank', 'christmas', 'boxing_day'], [], {}],
  'GB-SCT': ['Scotland', ['new_year', 'jan_2', 'good_friday', 'early_may', 'spring_bank', 'summer_bank_scot', 'st_andrew', 'christmas', 'boxing_day'], ['easter_monday'], { easter_monday: 'some employers' }],
  'GB-NIR': ['Northern Ireland', ['new_year', 'st_patrick', 'good_friday', 'easter_monday', 'early_may', 'spring_bank', 'boyne', 'summer_bank', 'christmas', 'boxing_day'], [], {}],
};
const US_GOV = 'federal government';
const US_REGIONS = {
  US: ['the US', ['new_year', 'memorial_day', 'independence_day', 'labor_day', 'thanksgiving_us', 'christmas'],
    ['mlk_day', 'presidents_day', 'juneteenth', 'columbus_day', 'veterans_day', 'day_after_thanksgiving', 'christmas_eve'],
    { mlk_day: US_GOV, presidents_day: US_GOV, juneteenth: US_GOV, columbus_day: US_GOV, veterans_day: US_GOV, day_after_thanksgiving: 'many employers', christmas_eve: 'some employers' }],
};
const REGIONS = { ...CA_REGIONS, ...UK_REGIONS, ...US_REGIONS };

/** Every region, for the "Change" list: [{ code, name, country }]. */
export const REGION_LIST = Object.entries(REGIONS).map(([code, r]) => ({ code, name: r[0], country: code.slice(0, 2) }));
export function regionName(code) { return REGIONS[code] ? REGIONS[code][0] : null; }
export function hasHolidays(code) { return !!REGIONS[code]; }

/** The words for a region: UK people get bank holidays and holiday; everyone else public holidays and vacation. */
export function wordsFor(code) {
  const uk = String(code || '').startsWith('GB');
  return {
    publicHolidays: uk ? 'Bank holidays' : 'Public holidays',
    leave: uk ? 'Holiday' : 'Vacation',
    leaveDays: uk ? 'holiday days' : 'vacation days',
    leaveDay: uk ? 'holiday day' : 'vacation day',
    dayOff: uk ? 'Bank holiday' : 'Day off',
  };
}

/** From the home town Google gave back: country 'CA' and region 'Ontario' → 'CA-ON'. Null outside CA, the UK and the US. */
export function regionFromPlace(country, region) {
  const c = String(country || '').toUpperCase();
  if (c === 'US') return 'US';
  const want = String(region || '').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const pool = c === 'CA' ? CA_REGIONS : c === 'GB' ? UK_REGIONS : null;
  if (!pool) return null;
  for (const [code, r] of Object.entries(pool)) if (r[0].toLowerCase() === want || code.slice(3).toLowerCase() === want) return code;
  if (c === 'CA') return want === 'quebec' ? 'CA-QC' : null;
  return null;
}
/** For a household saved before home_region existed: "Hamilton, Ontario" → 'CA-ON'. */
export function regionFromTown(town, country) {
  const part = String(town || '').split(',').pop();
  if (country) return regionFromPlace(country, part);
  return regionFromPlace('CA', part) || regionFromPlace('GB', part);
}

const US_STATES = new Set(['alabama', 'alaska', 'arizona', 'arkansas', 'california', 'colorado', 'connecticut', 'delaware', 'florida',
  'georgia', 'hawaii', 'idaho', 'illinois', 'indiana', 'iowa', 'kansas', 'kentucky', 'louisiana', 'maine', 'maryland', 'massachusetts',
  'michigan', 'minnesota', 'mississippi', 'missouri', 'montana', 'nebraska', 'nevada', 'new hampshire', 'new jersey', 'new mexico',
  'new york', 'north carolina', 'north dakota', 'ohio', 'oklahoma', 'oregon', 'pennsylvania', 'rhode island', 'south carolina',
  'south dakota', 'tennessee', 'texas', 'utah', 'vermont', 'virginia', 'washington', 'west virginia', 'wisconsin', 'wyoming',
  'district of columbia', 'usa', 'united states']);
/** Where home is, as a country: the home region first ('CA-ON' → 'CA', 'GB-SCT' → 'GB', 'US'), then the town
 *  ("Horringer, England" → 'GB', "Austin, Texas" → 'US'), then couples.country_code — which only Curated sets, for
 *  streaming, and can be somewhere else. Null when none of them says. */
export function countryOfHome(region, town, code) {
  const r = String(region || '').toUpperCase();
  if (/^(CA|GB|US)(-|$)/.test(r)) return r.slice(0, 2);
  const t = regionFromTown(town);
  if (t) return t.slice(0, 2);
  if (US_STATES.has(String(town || '').split(',').pop().trim().toLowerCase())) return 'US';
  const c = String(code || '').toUpperCase();
  return /^[A-Z]{2}$/.test(c) ? c : null;
}

/** One region's holidays in year y: [{ key, name, date, observed, core, hint }] in date order. */
export function holidaysInYear(code, y) {
  const r = REGIONS[code];
  if (!r) return [];
  const [, core, others, hints] = r;
  const list = core.map((k) => [k, true]).concat(others.map((k) => [k, false])).map(([key, isCore]) => {
    const [name, rule] = RULES[key];
    return { key, name, date: rule(y), core: isCore, hint: hints[key] || null };
  }).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  // Move the ones on a weekend.
  const us = code === 'US';
  const taken = new Set(list.map((h) => h.date));
  for (const h of list) {
    const wd = weekday(h.date);
    h.observed = h.date;
    if (wd < 5 || NO_SHIFT.has(h.key)) continue;
    if (us) { h.observed = addDays(h.date, wd === 5 ? -1 : 1); continue; }
    let d = addDays(h.date, 7 - wd);
    while (taken.has(d) || weekday(d) >= 5) d = addDays(d, 1);
    h.observed = d; taken.add(d);
  }
  return list;
}

/** Each of the region's holidays once, at its next date on or after `today` (the Days off list). */
export function nextYearOf(code, today) {
  const y = Number(today.slice(0, 4));
  const seen = new Set(), out = [];
  for (const h of [...holidaysInYear(code, y), ...holidaysInYear(code, y + 1)]) {
    if (h.observed < today || seen.has(h.key)) continue;
    seen.add(h.key); out.push(h);
  }
  return out.sort((a, b) => (a.date < b.date ? -1 : 1));
}
export function coreKeys(code) { return REGIONS[code] ? REGIONS[code][1].slice() : []; }

/** The days off a person gets between `from` and `to` (inclusive): { 'YYYY-MM-DD': { key, name, date } } on the day it's taken. */
export function daysOffBetween(code, keys, from, to) {
  if (!code || !REGIONS[code] || !keys || !keys.length) return {};
  const want = new Set(keys);
  const out = {};
  const y0 = Number(from.slice(0, 4)) - 1, y1 = Number(to.slice(0, 4)) + 1;
  for (let y = y0; y <= y1; y++) {
    for (const h of holidaysInYear(code, y)) {
      if (want.has(h.key) && h.observed >= from && h.observed <= to) out[h.observed] = { key: h.key, name: h.name, date: h.date };
    }
  }
  return out;
}

/** "Sat 26 Dec — off Mon 28" / "Fri 25 Dec". */
const SHORT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function holidayWhen(h) {
  const f = (s) => SHORT_DAYS[weekday(s)] + ' ' + Number(s.slice(8, 10)) + ' ' + SHORT_MONTHS[Number(s.slice(5, 7)) - 1];
  if (h.observed === h.date) return f(h.date);
  return f(h.date) + ' — off ' + SHORT_DAYS[weekday(h.observed)] + ' ' + Number(h.observed.slice(8, 10));
}

/** The vacation year around `today`: [start, end) as dates, from the month it starts (1–12). */
export function leaveYear(today, startMonth = 1) {
  const y = Number(today.slice(0, 4)), m = Number(today.slice(5, 7));
  const sy = m >= startMonth ? y : y - 1;
  return [ymd(sy, startMonth, 1), ymd(sy + 1, startMonth, 1)];
}
/** Vacation days left: what you said was left (on the day you said it), less the vacation days
    marked since then this year; in a new year, the full allowance less those marked. null if unknown. */
export function leaveLeft(profile, vacationDates, today) {
  const p = profile || {};
  const [start, end] = leaveYear(today, p.vacation_year_start || 1);
  const marked = (from) => (vacationDates || []).filter((d) => d >= from && d < end).length;
  if (p.vacation_left != null && p.vacation_left_on && p.vacation_left_on >= start && p.vacation_left_on < end) {
    return Math.max(0, p.vacation_left - marked(p.vacation_left_on));
  }
  if (p.vacation_days != null) return Math.max(0, p.vacation_days - marked(start));
  return null;
}
